import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from 'bootstrap';

interface CountryConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  countryName: string;
  isLoading: boolean;
}

/**
 * Modal for confirming country selection in the withdrawal setup process.
 * Warns users that their country selection cannot be changed later.
 */
const CountryConfirmationModal: React.FC<CountryConfirmationModalProps> = ({
  isOpen, 
  onClose, 
  onConfirm, 
  countryName,
  isLoading
}) => {
  const { t } = useTranslation(['withdrawals']);
  const modalRef = useRef<HTMLDivElement>(null);
  const bsModal = useRef<Modal | null>(null);
  
  // Setup modal event listeners
  useEffect(() => {
    const modalElement = modalRef.current;
    if (!modalElement) return;
    
    const handleHidden = () => {
      onClose();
    };
    
    modalElement.addEventListener('hidden.bs.modal', handleHidden);
    
    return () => {
      modalElement.removeEventListener('hidden.bs.modal', handleHidden);
    };
  }, [onClose]);
  
  // Handle modal initialization and showing/hiding
  useEffect(() => {
    const modalElement = modalRef.current;
    if (!modalElement) return;
    
    // Initialize Bootstrap modal if it doesn't exist yet
    if (!bsModal.current) {
      bsModal.current = new Modal(modalElement, {
        backdrop: 'static',
        keyboard: true
      });
    }
    
    // Show or hide based on isOpen prop
    if (isOpen) {
      bsModal.current.show();
    }
    
    // Clean up on unmount only, not on every effect run
    return () => {
      // Nothing to do here - we'll clean up in the component unmount effect
    };
  }, [isOpen]);
  
  // Handle final cleanup on component unmount
  useEffect(() => {
    return () => {
      // Only try to dispose if we have a modal instance
      if (bsModal.current) {
        try {
          // Hide first
          const modalBackdrop = document.querySelector('.modal-backdrop');
          if (modalBackdrop) {
            modalBackdrop.remove();
          }
          
          // Then dispose
          bsModal.current.dispose();
        } catch (error) {
          console.error('Error disposing modal:', error);
        }
        bsModal.current = null;
      }
    };
  }, []);
  
  // Handle confirm button click
  const handleConfirm = () => {
    // First hide the modal via Bootstrap
    if (bsModal.current) {
      bsModal.current.hide();
    }
    
    // Then call the onConfirm callback
    onConfirm();
  };
  
  return (
    <div 
      className="modal fade"
      tabIndex={-1} 
      role="dialog"
      ref={modalRef}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content shadow">
          <div className="modal-header border-0">
            <h5 className="modal-title fw-bold">
              {t('withdrawals.confirmCountryTitle', 'Confirm Country Selection')}
            </h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label={t('common.close', 'Close')}
            ></button>
          </div>
          <div className="modal-body">
            <div className="alert alert-warning border-0 mb-3">
              <div className="d-flex">
                <i className="bi bi-exclamation-triangle-fill fs-3 me-3 text-warning"></i>
                <div>
                  <h6 className="mb-2 fw-bold">{t('withdrawals.importantNotice', 'Important Notice')}</h6>
                  <p className="mb-0">
                    {t('withdrawals.countryConfirmMessage', 'Once you confirm your country selection, you will not be able to change it later. This is because withdrawal methods and verification requirements are specific to each country.')}
                  </p>
                </div>
              </div>
            </div>
            
            <p className="mb-0">
              {t('withdrawals.selectedCountry', 'You have selected:')} <strong>{countryName || '-'}</strong>
            </p>
            
            <div className="alert alert-info border-0 mt-3 small">
              <i className="bi bi-info-circle-fill me-2"></i>
              {t('withdrawals.notFoundCountryMessage', "If you don't see your country in the list, it means we don't support withdrawals for your country yet. Please check back later as we're expanding our coverage.")}
            </div>
          </div>
          <div className="modal-footer border-0">
            <button
              type="button"
              className="btn btn-outline-secondary"
              data-bs-dismiss="modal"
              aria-label={t('common.cancel', 'Cancel')}
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleConfirm}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  {t('common.processing', 'Processing...')}
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle me-2"></i>
                  {t('withdrawals.confirmCountry', 'Confirm Country')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CountryConfirmationModal; 